import { forwardRef, useImperativeHandle, useState } from "react";
import PrintTemplateService from '../../services/PrintTemplateService';

const CustomerStatementReceipt = forwardRef((props, ref) => {
    const [data, setData] = useState(null);
    const [template, setTemplate] = useState('');
    const [mode, setMode] = useState('statement_simple'); // 'statement_simple', 'statement_detailed'

    const isTemplateEmpty = (html) => {
        if (!html) return true;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        doc.querySelectorAll('br').forEach(x => x.remove());
        const text = (doc.body.textContent || '').replace(/\u00a0/g, '').replace(/\u200B/g, '').trim();
        return text.length === 0;
    };

    const print = async (customerDetail, printMode = 'statement_simple') => {
        if (!customerDetail) return;
        try {
            // Get custom template from backend
            const templateRes = await PrintTemplateService.getTemplate(printMode);
            let content = '';

            if (templateRes.success && templateRes.data && !isTemplateEmpty(templateRes.data.content)) {
                content = templateRes.data.content;
            } else {
                content = getFallbackTemplate(printMode);
            }

            setData(customerDetail);
            setMode(printMode);
            setTemplate(processTemplate(content, customerDetail, printMode));

            setTimeout(() => {
                window.print();
            }, 1000);
        } catch (error) {
            console.error("Yazdırma sırasında hata:", error);
        }
    };

    const fmtMoney = (v) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(v ?? 0);

    const getFallbackTemplate = (mode) => {
        if (mode === 'statement_simple') {
            return `
            <div style="text-align: center; border-bottom: 1px dashed #000; padding-bottom: 5px; margin-bottom: 10px;">
                <div style="font-size: 14pt; font-weight: bold; text-transform: uppercase;">{{FirmaAdi}}</div>
                <div style="font-size: 10pt;">{{FirmaAdresi}}</div>
                <div style="font-size: 10pt;">{{FirmaTelefon}}</div>
                <div style="font-size: 12pt; margin-top: 5px; font-weight: bold; text-decoration: underline;">HESAP EKSTRESİ (ÖZET)</div>
            </div>
            <div style="font-size: 10pt; line-height: 1.2; margin-bottom: 10px;">
                <div><strong>Tarih:</strong> {{GuncelTarih}}</div>
                <div><strong>Müşteri:</strong> {{MusteriAdi}}</div>
                <div><strong>Telefon:</strong> {{MusteriTelefon}}</div>
            </div>
            <div style="border-top: 1px solid #000; margin-top: 10px; padding-top: 5px;">
                <div style="display: flex; justify-content: space-between; font-size: 11pt; margin-bottom: 5px;">
                    <span>TOPLAM SATIŞ:</span>
                    <span>{{ToplamSatis}}</span>
                </div>
                <div style="display: flex; justify-content: space-between; font-size: 11pt; margin-bottom: 5px;">
                    <span>TOPLAM TAHSİLAT:</span>
                    <span>{{ToplamTahsilat}}</span>
                </div>
                <div style="display: flex; justify-content: space-between; font-size: 12pt; font-weight: bold; border-top: 2px solid #000; padding-top: 5px;">
                    <span>GÜNCEL BAKİYE:</span>
                    <span>{{MusteriBakiyesi}}</span>
                </div>
            </div>`;
        } else {
            return `
            <div style="text-align: center; border-bottom: 1px dashed #000; padding-bottom: 5px; margin-bottom: 10px;">
                <div style="font-size: 14pt; font-weight: bold; text-transform: uppercase;">{{FirmaAdi}}</div>
                <div style="font-size: 10pt;">{{FirmaAdresi}}</div>
                <div style="font-size: 10pt;">{{FirmaTelefon}}</div>
                <div style="font-size: 12pt; margin-top: 5px; font-weight: bold; text-decoration: underline;">HESAP EKSTRESİ (DETAYLI)</div>
            </div>
            <div style="font-size: 10pt; line-height: 1.2; margin-bottom: 10px;">
                <div><strong>Tarih:</strong> {{GuncelTarih}}</div>
                <div><strong>Müşteri:</strong> {{MusteriAdi}}</div>
                <div><strong>Telefon:</strong> {{MusteriTelefon}}</div>
            </div>

            <div style="font-weight: bold; font-size: 11pt; margin-top: 10px; border-bottom: 1px solid #000;">TAHSİLATLAR</div>
            {{TahsilatTablosu}}

            <div style="font-weight: bold; font-size: 11pt; margin-top: 15px; border-bottom: 1px solid #000;">SATIŞLAR</div>
            {{SatisTablosu}}

            <div style="border-top: 2px solid #000; margin-top: 15px; padding-top: 5px; font-weight: bold;">
                <div style="display: flex; justify-content: space-between; font-size: 12pt;">
                    <span>GÜNCEL BAKİYE:</span>
                    <span>{{MusteriBakiyesi}}</span>
                </div>
            </div>`;
        }
    };

    const processTemplate = (html, detail, printMode) => {
        let result = html;
        const replacements = {
            '{{FirmaAdi}}': detail.companyName || "---",
            '{{FirmaAdresi}}': detail.companyAddress || "---",
            '{{FirmaTelefon}}': detail.companyPhone || "---",
            '{{GuncelTarih}}': new Date().toLocaleString("tr-TR"),
            '{{MusteriAdi}}': detail.customer?.name || "---",
            '{{MusteriTelefon}}': detail.customer?.phone || "---",
            '{{MusteriBakiyesi}}': fmtMoney(detail.summary?.balance),
            '{{ToplamSatis}}': fmtMoney(detail.summary?.totalSales),
            '{{ToplamTahsilat}}': fmtMoney(detail.summary?.totalPaid),
            '{{IslemTablosu}}': renderTransactionTable(detail),
            '{{TahsilatTablosu}}': renderCollectionTable(detail),
            '{{SatisTablosu}}': renderSalesTable(detail)
        };

        Object.keys(replacements).forEach(key => {
            result = result.replace(new RegExp(key, 'g'), replacements[key]);
        });
        return result;
    };

    const renderTransactionTable = (detail) => {
        return `
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt; margin-bottom: 10px;">
                <thead>
                    <tr style="border-bottom: 1px solid black;">
                        <th style="text-align: left; padding: 2px;">Tarih</th>
                        <th style="text-align: left; padding: 2px;">İşlem</th>
                        <th style="text-align: right; padding: 2px;">Tutar</th>
                        <th style="text-align: right; padding: 2px;">Bakiye</th>
                    </tr>
                </thead>
                <tbody>
                    ${(detail.allTransactions || []).map(t => `
                        <tr>
                            <td style="padding: 2px; border-bottom: 0.1mm solid #eee;">${t.date}</td>
                            <td style="padding: 2px; border-bottom: 0.1mm solid #eee;">
                                <div>${t.transactionType}</div>
                                <div style="font-size: 7pt; color: #666;">${t.description || ''}</div>
                            </td>
                            <td style="text-align: right; padding: 2px; border-bottom: 0.1mm solid #eee;">${fmtMoney(t.amount)}</td>
                            <td style="text-align: right; padding: 2px; border-bottom: 0.1mm solid #eee; font-weight: bold;">${fmtMoney(t.balance)}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        `;
    };

    const renderCollectionTable = (detail) => {
        return `
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt; margin-bottom: 10px;">
                <thead>
                    <tr style="border-bottom: 1px solid black;">
                        <th style="text-align: left; padding: 2px;">Tarih</th>
                        <th style="text-align: left; padding: 2px;">Tip</th>
                        <th style="text-align: right; padding: 2px;">Tutar</th>
                    </tr>
                </thead>
                <tbody>
                    ${(detail.payments || []).map(p => `
                        <tr>
                            <td style="padding: 2px; border-bottom: 0.1mm solid #eee;">${p.date}</td>
                            <td style="padding: 2px; border-bottom: 0.1mm solid #eee;">${p.paymentType}</td>
                            <td style="text-align: right; padding: 2px; border-bottom: 0.1mm solid #eee; font-weight: bold;">${fmtMoney(p.amount)}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        `;
    };

    const renderSalesTable = (detail) => {
        return `
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt; margin-bottom: 10px;">
                <thead>
                    <tr style="border-bottom: 1px solid black;">
                        <th style="text-align: left; padding: 2px;">Tarih</th>
                        <th style="text-align: left; padding: 2px;">Fatura No</th>
                        <th style="text-align: right; padding: 2px;">Tutar</th>
                    </tr>
                </thead>
                <tbody>
                    ${(detail.invoices || []).map(inv => `
                        <tr>
                            <td style="padding: 2px; border-bottom: 0.1mm solid #eee;">${inv.saleDate}</td>
                            <td style="padding: 2px; border-bottom: 0.1mm solid #eee;">${inv.saleNo}</td>
                            <td style="text-align: right; padding: 2px; border-bottom: 0.1mm solid #eee; font-weight: bold;">${fmtMoney(inv.totalAmount)}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
        `;
    };

    useImperativeHandle(ref, () => ({
        print
    }));

    if (!data) return null;

    return (
        <div className="print-only">
            <style type="text/css">
                {`
                .print-only { display: none; }
                @media print {
                    @page { size: 76mm auto; margin: 0 !important; }
                    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; color: #000 !important; }
                    body { width: 76mm !important; }
                    .print-only {
                        display: block !important;
                        width: 72mm !important;
                        margin: 0 !important;
                        padding: 2mm !important;
                        font-family: Verdana, sans-serif;
                        font-size: 8pt;
                        font-weight: 600;
                        line-height: 1.15;
                        box-sizing: border-box;
                    }
                    .no-print { display: none !important; }
                }
                `}
            </style>
            <div dangerouslySetInnerHTML={{ __html: template }} />
        </div>
    );
});

export default CustomerStatementReceipt;
